import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
	ArrowBackIosNew as PreviousIcon,
	ArrowForwardIos as NextIcon,
	AttachFile as AttachFileIcon,
	FileDownload as DownloadIcon,
	OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';

import DialogHeaderActions from '../DialogHeaderActions';
import { ChatAttachment } from './PromptChatAdapter';
import { CHAT_HTML_CONTENT_TYPE } from './ChatHtmlConstants';

export type AttachmentPreviewKind = 'image' | 'pdf' | 'text' | 'other';

const IMAGE_EXTENSIONS = new Set(['gif', 'jpeg', 'jpg', 'png', 'webp']);
const TEXT_EXTENSIONS = new Set(['csv', 'json', 'md', 'txt', 'xml']);

export function getAttachmentPreviewKind(attachment: ChatAttachment): AttachmentPreviewKind {
	const contentType = attachment.contentType?.split(';', 1)[0].trim().toLowerCase();
	if (contentType?.startsWith('image/')) return 'image';
	if (contentType === 'application/pdf') return 'pdf';
	if (contentType === CHAT_HTML_CONTENT_TYPE) return 'other';
	if (contentType?.startsWith('text/') || contentType === 'application/json' || contentType === 'application/xml') return 'text';

	const extension = attachment.fileName.split('.').pop()?.toLowerCase();
	if (extension && IMAGE_EXTENSIONS.has(extension)) return 'image';
	if (extension === 'pdf') return 'pdf';
	if (extension && TEXT_EXTENSIONS.has(extension)) return 'text';
	return 'other';
}

export type AttachmentUrlResolver = (attachment: ChatAttachment) => Promise<string>;

export function downloadChatAttachment(url: string, fileName: string) {
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	link.rel = 'noopener noreferrer';
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	link.remove();
}

function useAttachmentUrl(attachment: ChatAttachment | undefined, resolveUrl: AttachmentUrlResolver) {
	const [url, setUrl] = useState<string>();
	const [error, setError] = useState(false);
	const fileId = attachment?.fileId;
	const fileName = attachment?.fileName;
	const contentType = attachment?.contentType;
	const size = attachment?.size;
	// Message deltas can recreate attachment objects without changing the underlying file.
	const stableAttachment = useMemo(() => fileId === undefined ? undefined : {
		fileId, fileName: fileName ?? '', contentType, size,
	}, [fileId, fileName, contentType, size]);

	useEffect(() => {
		let active = true;
		setUrl(undefined);
		setError(false);
		if (!stableAttachment) return () => { active = false; };

		void resolveUrl(stableAttachment)
			.then((resolvedUrl) => {
				if (active) setUrl(resolvedUrl);
			})
			.catch(() => {
				if (active) setError(true);
			});

		return () => { active = false; };
	}, [stableAttachment, resolveUrl]);

	return { url, error };
}

export function ChatAttachmentThumbnail({
	attachment,
	resolveUrl,
	onClick,
}: {
	attachment: ChatAttachment;
	resolveUrl: AttachmentUrlResolver;
	onClick: () => void;
}) {
	const rootRef = useRef<HTMLButtonElement | null>(null);
	const [shouldLoad, setShouldLoad] = useState(false);
	const { url, error } = useAttachmentUrl(shouldLoad ? attachment : undefined, resolveUrl);

	useEffect(() => {
		const element = rootRef.current;
		if (!element || typeof IntersectionObserver === 'undefined') {
			setShouldLoad(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setShouldLoad(true);
					observer.disconnect();
				}
			},
			{ rootMargin: '160px' },
		);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	return (
		<Button
			ref={rootRef}
			variant="outlined"
			onClick={onClick}
			aria-label={`Preview ${attachment.fileName}`}
			sx={{
				p: 0,
				minWidth: 0,
				height: 96,
				overflow: 'hidden',
				position: 'relative',
				borderColor: 'divider',
				bgcolor: 'background.default',
				textTransform: 'none',
			}}
		>
			{url && !error ? (
				<Box component="img" src={url} alt={attachment.fileName} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
			) : error ? (
				<AttachFileIcon color="disabled" />
			) : (
				<CircularProgress size={18} />
			)}
			<Box
				sx={{
					position: 'absolute',
					left: 0,
					right: 0,
					bottom: 0,
					px: 0.75,
					py: 0.35,
					bgcolor: 'rgba(0,0,0,0.62)',
					color: 'common.white',
					fontSize: '0.68rem',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				}}
			>
				{attachment.fileName}
			</Box>
		</Button>
	);
}

export type AttachmentPreviewSelection = {
	attachment: ChatAttachment;
	attachments: ChatAttachment[];
};

export function ChatAttachmentPreviewDialog({
	selection,
	resolveUrl,
	onClose,
}: {
	selection: AttachmentPreviewSelection;
	resolveUrl: AttachmentUrlResolver;
	onClose: () => void;
}) {
	const gallery = useMemo(() => {
		if (getAttachmentPreviewKind(selection.attachment) !== 'image') return [selection.attachment];
		return selection.attachments.filter((attachment) => getAttachmentPreviewKind(attachment) === 'image');
	}, [selection]);
	const initialIndex = Math.max(0, gallery.findIndex((attachment) => attachment.fileId === selection.attachment.fileId));
	const [activeIndex, setActiveIndex] = useState(initialIndex);
	const [isMaximized, setIsMaximized] = useState(false);
	const activeAttachment = gallery[activeIndex] ?? gallery[0];
	const previewKind = getAttachmentPreviewKind(activeAttachment);
	const { url, error } = useAttachmentUrl(activeAttachment, resolveUrl);
	const hasGallery = gallery.length > 1;

	const selectRelative = (offset: number) => {
		setActiveIndex((current) => (current + offset + gallery.length) % gallery.length);
	};

	return (
		<Dialog
			open
			onClose={onClose}
			fullWidth
			maxWidth="lg"
			fullScreen={isMaximized}
			PaperProps={{ sx: isMaximized ? { height: '100vh', maxHeight: '100vh' } : { height: 'min(860px, 92vh)' } }}
		>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
				<Typography component="span" variant="h6" noWrap sx={{ flex: 1, minWidth: 0 }} title={activeAttachment.fileName}>
					{activeAttachment.fileName}
				</Typography>
				<DialogHeaderActions
					isMaximized={isMaximized}
					onToggleMaximized={() => setIsMaximized((value) => !value)}
					onClose={onClose}
				>
					{url && (
						<>
							<Tooltip title="Open original in a new tab">
								<IconButton component="a" href={url} target="_blank" rel="noopener noreferrer" aria-label="Open original attachment" size="small">
									<OpenInNewIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip title={`Download ${activeAttachment.fileName}`}>
								<IconButton
									aria-label={`Download ${activeAttachment.fileName}`}
									onClick={() => downloadChatAttachment(url, activeAttachment.fileName)}
									size="small"
								>
									<DownloadIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</>
					)}
				</DialogHeaderActions>
			</DialogTitle>
			<DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: 'background.default' }}>
				<Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
					{error ? (
						<Alert severity="error">The attachment preview could not be loaded.</Alert>
					) : !url ? (
						<CircularProgress />
					) : previewKind === 'image' ? (
						<Box component="img" src={url} alt={activeAttachment.fileName} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
					) : previewKind === 'pdf' || previewKind === 'text' ? (
						<Box component="iframe" src={url} title={activeAttachment.fileName} sx={{ border: 0, width: '100%', height: '100%', bgcolor: 'common.white' }} />
					) : (
						<Box sx={{ display: 'grid', justifyItems: 'center', gap: 1.5, p: 3 }}>
							<AttachFileIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
							<Typography color="text.secondary">A preview is not available for this file type.</Typography>
							<Button component="a" href={url} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />}>
								Open original
							</Button>
						</Box>
					)}
					{hasGallery && (
						<>
							<IconButton onClick={() => selectRelative(-1)} aria-label="Previous image" sx={{ position: 'absolute', left: 12, bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'background.paper' } }}>
								<PreviousIcon />
							</IconButton>
							<IconButton onClick={() => selectRelative(1)} aria-label="Next image" sx={{ position: 'absolute', right: 12, bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'background.paper' } }}>
								<NextIcon />
							</IconButton>
						</>
					)}
				</Box>
				{hasGallery && (
					<Box sx={{ display: 'flex', gap: 1, p: 1, overflowX: 'auto', borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
						{gallery.map((attachment, index) => (
							<Box key={attachment.fileId} sx={{ width: 112, flex: '0 0 auto', outline: index === activeIndex ? '2px solid' : 'none', outlineColor: 'primary.main', borderRadius: 1 }}>
								<ChatAttachmentThumbnail attachment={attachment} resolveUrl={resolveUrl} onClick={() => setActiveIndex(index)} />
							</Box>
						))}
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
}
